import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';
import { SuperAdminShell } from './SuperAdminDashboard';
import { Button, Badge, Spinner } from '../../components/shared/UI';
import API from '../../utils/apiClient';

export default function SuperAdminAttendancePage() {
  const { addToast } = useToast();
  const [cohorts, setCohorts]   = useState([]);
  const [selected, setSelected] = useState(null);
  const [report, setReport]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [repLoading, setRepL]   = useState(false);
  const [sending, setSending]   = useState(false);

  useEffect(() => {
    API.get('/cohorts').then(({data}) => setCohorts(data.cohorts||[])).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const loadReport = async (cohortId) => {
    setSelected(cohortId); setRepL(true);
    try {
      const { data } = await API.get(`/attendance/cohort/${cohortId}/report`);
      setReport(data);
    } catch { addToast('Failed to load report','error'); }
    finally { setRepL(false); }
  };

  const sendWarnings = async () => {
    if (!selected) return;
    setSending(true);
    try {
      const { data } = await API.post('/attendance/send-warnings', { cohortId: selected });
      addToast(data.message, 'success');
    } catch (e) { addToast(e.message||'Failed','error'); }
    finally { setSending(false); }
  };

  const pctColor = p => p >= 80 ? 'text-green-600' : p >= 60 ? 'text-yellow-600' : 'text-brand-500';
  const pctBg   = p => p >= 80 ? 'bg-green-50 border-green-200' : p >= 60 ? 'bg-yellow-50 border-yellow-200' : 'bg-brand-50 border-brand-200';

  return (
    <SuperAdminShell title="Attendance" subtitle="Monitor attendance across all cohorts">
      <div className="space-y-6">
        {/* Cohort selector */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-de-black mb-3">Select Cohort</h3>
          {loading ? <Spinner className="text-brand-500"/> : (
            <div className="flex gap-2 flex-wrap">
              {cohorts.map(c => (
                <button key={c.id} onClick={() => loadReport(c.id)}
                  className={`px-3 py-2 rounded-xl text-xs font-semibold border-2 transition-all
                    ${selected===c.id?'border-brand-500 bg-brand-50 text-brand-600':'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                  {c.name} <span className="text-gray-400">({c.level})</span>
                </button>
              ))}
              {cohorts.length === 0 && <p className="text-sm text-gray-400">No cohorts yet.</p>}
            </div>
          )}
        </div>

        {/* Report */}
        {selected && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="text-sm font-bold text-de-black">Attendance Report</h3>
                {report?.atRisk?.length > 0 && (
                  <p className="text-xs text-brand-500 font-semibold">{report.atRisk.length} student(s) below 80%</p>
                )}
              </div>
              <Button variant="danger" size="sm" loading={sending} onClick={sendWarnings}>
                ⚠️ Send Warnings to At-Risk Students
              </Button>
            </div>

            {repLoading ? (
              <div className="flex justify-center py-8"><Spinner className="text-brand-500"/></div>
            ) : report?.students?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {['Student ID','Name','Total','Present','Late','Absent','Attendance %','Status'].map(h => (
                        <th key={h} className="text-left py-2.5 px-4 text-xs font-bold text-gray-500 uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {report.students.map((s, i) => {
                      const pct = s.attendance_pct || 0;
                      return (
                        <tr key={s.student_id} className={`border-b border-gray-100 hover:bg-gray-50 ${i%2?'bg-gray-50/40':''}`}>
                          <td className="py-2.5 px-4 font-mono text-xs font-bold text-brand-500">{s.student_number||'—'}</td>
                          <td className="py-2.5 px-4 text-xs font-semibold text-de-black">{s.name}</td>
                          <td className="py-2.5 px-4 text-xs text-gray-600">{s.total_classes||0}</td>
                          <td className="py-2.5 px-4 text-xs text-green-600 font-semibold">{s.present_count||0}</td>
                          <td className="py-2.5 px-4 text-xs text-yellow-600 font-semibold">{s.late_count||0}</td>
                          <td className="py-2.5 px-4 text-xs text-brand-500 font-semibold">{s.absent_count||0}</td>
                          <td className="py-2.5 px-4">
                            <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-bold ${pctBg(pct)}`}>
                              <span className={pctColor(pct)}>{pct}%</span>
                            </div>
                          </td>
                          <td className="py-2.5 px-4">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${pct >= 80 ? 'bg-green-100 text-green-700' : 'bg-brand-50 text-brand-600'}`}>
                              {pct >= 80 ? 'Good' : '⚠ At Risk'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-8">No attendance records yet for this cohort.</p>
            )}
          </div>
        )}
      </div>
    </SuperAdminShell>
  );
}
